/**
 * ConciergeHistoryPopover — clock-icon button (top-right of the Concierge
 * page) that opens a small floating list of the user's recent Concierge
 * sessions, exactly like the Agent-of-Agents history button.
 *
 * It deliberately reuses the GLOBAL `aoa-header-icon-btn` / `aoa-history-*`
 * styles (bundled app-wide via the statically-imported AoA page) so the two
 * surfaces look identical. The Concierge list endpoint has no server-side
 * search, so — unlike AoA — there is no search box here; just a scrollable
 * list with scroll-to-load-more. Keep it simple.
 */

import React, { useRef, useState } from 'react';
import { activeLocale } from '@/utils/locale';
import { Popover, Tooltip } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import type { ConciergeConversation } from '../types';
import { mirrorPlacement } from '@/utils/rtl';

interface Props {
  sessions: ConciergeConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const HistoryContent: React.FC<Props & { close: () => void }> = ({
  sessions, activeId, onSelect, close, loading, hasMore, loadingMore, onLoadMore,
}) => {
  const { t } = useLanguage();
  const listRef = useRef<HTMLDivElement | null>(null);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      if (hasMore && !loadingMore && onLoadMore) onLoadMore();
    }
  };

  return (
    <div className="aoa-history">
      <div ref={listRef} className="aoa-history-list" onScroll={onScroll} data-testid="concierge-history-list">
        {loading && sessions.length === 0 && (
          <div className="aoa-history-empty">{t('common.loading') || 'Loading…'}</div>
        )}
        {!loading && sessions.length === 0 && (
          <div className="aoa-history-empty">
            {t('concierge.history.empty') || 'No conversations yet.'}
          </div>
        )}
        {sessions.map((s) => {
          const isActive = s.conversation_id === activeId;
          return (
            <button
              key={s.conversation_id}
              type="button"
              className={`aoa-history-item ${isActive ? 'is-active' : ''}`}
              onClick={() => { onSelect(s.conversation_id); close(); }}
              title={s.conversation_title || ''}
            >
              <span className="aoa-history-item-title" dir="auto">
                {s.conversation_title || t('concierge.history.untitled') || 'New conversation'}
              </span>
              {s.last_message_at && (
                <span className="aoa-history-item-time">{formatRelative(s.last_message_at)}</span>
              )}
            </button>
          );
        })}
        {loadingMore ? (
          <div className="aoa-history-more">{t('common.loading') || 'Loading more…'}</div>
        ) : hasMore ? (
          <div className="aoa-history-more">{t('concierge.history.scrollMore') || 'Scroll for more…'}</div>
        ) : null}
      </div>
    </div>
  );
};

export const ConciergeHistoryPopover: React.FC<Props> = (props) => {
  const { t, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  const label = t('concierge.history.title') || 'Conversation history';

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={mirrorPlacement(direction, 'bottomRight')}
      arrow={false}
      overlayClassName="aoa-history-popover"
      content={<HistoryContent {...props} close={() => setOpen(false)} />}
    >
      <Tooltip title={label} placement="bottom" overlayClassName="aoa-tooltip">
        <button
          type="button"
          className={`aoa-header-icon-btn ${open ? 'is-open' : ''}`}
          aria-label={label}
          data-testid="concierge-history-button"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </button>
      </Tooltip>
    </Popover>
  );
};

/** "just now" · "5m" · "2h" · "3d" · "Jan 4". Local-only formatting. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!then) return '';
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' });
}

export default ConciergeHistoryPopover;
